import { Email } from '@mui/icons-material';
import { ButtonBase, ButtonBaseProps, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { EmailShareButton, FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';
import { makeStyles } from 'tss-react/mui';
import { notNullOrEmpty } from '../../utils/common';
import { CopyLinkIcon, FacebookIcon, LinkedinIcon, ShareButtonIcon, TwitterIcon } from '../icons';
import { ShareButtonDesktopMenu } from './shareButtonDesktopMenu';
import { ShareButtonMobileMenu } from './shareButtonMobileMenu';
import { CopyUrlProps, EmailProps, FacebookProps, LinkedinProps, ShareLinkItem, StylesProps, TwitterProps } from './types';

const useStyles = makeStyles<StylesProps>({ name: 'ShareButton' })((theme, props) => ({
	button: {
		width: 44,
		height: 44,
		borderRadius: 22,
		backgroundColor: props.darkBackground ? '#F3F3F31F' : '#1617180F',
		color: props.darkBackground ? theme.palette.skodaColors['neutrals-white'] : theme.palette.text.primary
	}
}));

export type ShareButtonProps = ButtonBaseProps & {
	url?: string;
	copyUrlProps?: CopyUrlProps;
	facebookProps?: FacebookProps;
	twitterProps?: TwitterProps;
	linkedinProps?: LinkedinProps;
	emailProps?: EmailProps;
	darkBackground?: boolean;
	textConfirm: string;
	textCancel: string;
};

export const ShareButton: React.FC<ShareButtonProps> = (props) => {
	const { url, copyUrlProps, facebookProps, twitterProps, linkedinProps, emailProps,
		darkBackground, textConfirm, textCancel,
		className, onClick, ...other } = props;

	const { classes, cx } = useStyles({ darkBackground });

	const buttonRef = React.useRef<HTMLButtonElement>();
	const [menuOpen, setMenuOpen] = React.useState(false);

	const _url = notNullOrEmpty(url) ? url : window.location.href;

	const getUrl = (additionalUrlParameters: string) => {
		if (notNullOrEmpty(additionalUrlParameters)) {
			return _url.includes('?') ? `${_url}&${additionalUrlParameters}` : `${_url}?${additionalUrlParameters}`;
		}

		return _url;
	};
	const addStylesToOptionProps = (optionalProps: any, className: string) => ({ ...optionalProps, className: cx(optionalProps?.className, className) });

	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.originalBreakpoints.up('sm'));

	const emptyLabels = !copyUrlProps?.label && !facebookProps?.label && !twitterProps?.label && !linkedinProps?.label && !emailProps?.label;

	const createCopyUrlShareLink = (child: React.ReactNode, className: string) => {
		return (
			<CopyToClipboard 
				data-test='button-share-copylink'
				data-test-url={getUrl(copyUrlProps?.additionalUrlParameters)}
				text={getUrl(copyUrlProps?.additionalUrlParameters)} 
				onCopy={() => copyUrlProps?.onClick?.()}>
				<div className={className}>
					{child}
				</div>
			</CopyToClipboard>
		);
	};

	const createFacebookShareLink = (child: React.ReactNode, className: string) => {
		return (
			<FacebookShareButton 
				data-test='button-share-facebook'
				data-test-url={getUrl(facebookProps?.additionalUrlParameters)}
				url={getUrl(facebookProps?.additionalUrlParameters)} 
				{...addStylesToOptionProps(facebookProps?.optionalProps, className)}>
				{child}
			</FacebookShareButton>
		);
	};

	const createTwitterShareLink = (child: React.ReactNode, className: string) => {
		return (
			<TwitterShareButton
				data-test='button-share-twitter'
				data-test-url={getUrl(twitterProps?.additionalUrlParameters)}
				url={getUrl(twitterProps?.additionalUrlParameters)} 
				{...addStylesToOptionProps(twitterProps?.optionalProps, className)}>
				{child}
			</TwitterShareButton>
		);
	};

	const createLinkedinShareLink = (child: React.ReactNode, className: string) => {
		return (
			<LinkedinShareButton 
				data-test='button-share-linkedin'
				data-test-url={getUrl(linkedinProps?.additionalUrlParameters)}
				url={getUrl(linkedinProps?.additionalUrlParameters)} 
				{...addStylesToOptionProps(linkedinProps?.optionalProps, className)}>
				{child}
			</LinkedinShareButton>
		);
	};

	const createEmailShareLink = (child: React.ReactNode, className: string) => {
		return (
			<EmailShareButton 
				data-test='button-share-email'
				data-test-body={emailProps?.optionalProps.body} 
				data-test-subject={emailProps?.optionalProps.subject} 
				data-test-separator={emailProps?.optionalProps.separator} 
				data-test-url={getUrl(emailProps?.additionalUrlParameters)} 
				url={getUrl(emailProps?.additionalUrlParameters)} 
				{...addStylesToOptionProps(emailProps?.optionalProps, className)}>
				{child}
			</EmailShareButton>
		);
	};

	const shareLinkItems = [
		...!copyUrlProps?.hide ? [{ Icon: CopyLinkIcon, label: copyUrlProps?.label, createShareLink: createCopyUrlShareLink }] : [],
		...!facebookProps?.hide ? [{ Icon: FacebookIcon, label: facebookProps?.label, createShareLink: createFacebookShareLink }] : [],
		...!twitterProps?.hide ? [{ Icon: TwitterIcon, label: twitterProps?.label, createShareLink: createTwitterShareLink }] : [],
		...!linkedinProps?.hide ? [{ Icon: LinkedinIcon, label: linkedinProps?.label, createShareLink: createLinkedinShareLink }] : [],
		...!emailProps?.hide ? [{ Icon: Email, label: emailProps?.label, createShareLink: createEmailShareLink }] : []
	] as ShareLinkItem[];

	return (
		<React.Fragment>
			<ButtonBase
				data-test='button-share'
				ref={buttonRef}
				onClick={(e) => { onClick?.(e); setMenuOpen(true); }}
				className={cx(className, classes.button)}
				{...other}>
				<ShareButtonIcon />
			</ButtonBase>
			{isDesktop
				? <ShareButtonDesktopMenu
					anchorEl={buttonRef.current}
					open={menuOpen}
					onClose={() => setMenuOpen(false)}
					items={shareLinkItems} />
				: <ShareButtonMobileMenu
					anchorEl={buttonRef.current}
					open={menuOpen}
					onClose={() => setMenuOpen(false)}
					textCancel={textCancel}
					textConfirm={textConfirm}
					centerMenuItems={emptyLabels}
					items={shareLinkItems} />}
		</React.Fragment >
	);
};
